import { useQuery, useMutation } from '@tanstack/react-query';
import { userApiService } from '../services/userApi';
import { useAuthContext } from '@/providers/Auth';
import type {
  IdentityUser,
  UpdatePersonalData,
  UpdateIdentityUserResult,
} from '../types/user';

interface UseUserReturn {
  user: IdentityUser | null;
  getUser: () => Promise<IdentityUser | undefined>;
  isLoading: boolean;
  updatePersonalData: (
    personalData: UpdatePersonalData
  ) => Promise<UpdateIdentityUserResult>;
  isUpdatePending: boolean;
  updatePersonalDataError: Error | null;
}

export const useUser = (): UseUserReturn => {
  const authContext = useAuthContext();
  if (!authContext) {
    throw new Error('useUser must be used within an AuthProvider');
  }
  const { setUser, user } = authContext;

  const userQuery = useQuery<IdentityUser>({
    queryKey: ['user'],
    queryFn: () => userApiService.getUser(),
  });

  const updatePersonalDataMutation = useMutation({
    mutationFn: (personalData: UpdatePersonalData) =>
      userApiService.updatePersonalData(personalData),
    onSuccess: (result) => {
      if (result.user) {
        setUser(result.user);
      }
    },
  });

  const getUser = async (): Promise<IdentityUser | undefined> => {
    const result = await userQuery.refetch();
    if (result.isSuccess) {
      setUser(result.data);
    }
    return result.data;
  };

  return {
    user,
    getUser,
    isLoading: userQuery.isLoading,
    updatePersonalData: updatePersonalDataMutation.mutateAsync,
    isUpdatePending: updatePersonalDataMutation.isPending,
    updatePersonalDataError: updatePersonalDataMutation.error,
  };
};
